import React, { useState, useEffect, useCallback, useRef } from "react";
import ReactDOM from "react-dom";

const DEFAULTS = {
  bgColor:            "#ffffff",
  topBarHeight:       "0",
  topBarColor:        "#333333",
  topBarTextColor:    "#ffffff",
  topBarLabel:        "Client header",
  bottomBarHeight:    "0",
  bottomBarColor:     "#333333",
  bottomBarTextColor: "#ffffff",
  bottomBarLabel:     "Client footer",
  cfMaxWidth:         "",
  cfPadding:          "",
  cfBorder:           "",
  cfBorderRadius:     "",
  cfBoxShadow:        "",
};

function parseColorValue(value) {
  if (!value) return { hex: "#000000", alpha: 1 };
  const m = String(value).match(
    /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+))?\s*\)$/
  );
  if (m) {
    const hex =
      "#" +
      [m[1], m[2], m[3]]
        .map((n) => parseInt(n, 10).toString(16).padStart(2, "0"))
        .join("");
    return {
      hex,
      alpha: m[4] !== undefined ? Math.min(1, Math.max(0, parseFloat(m[4]))) : 1,
    };
  }
  if (/^#[0-9a-fA-F]{3}$/.test(value))
    return { hex: "#" + value.slice(1).split("").map((c) => c + c).join(""), alpha: 1 };
  if (/^#[0-9a-fA-F]{6}$/.test(value)) return { hex: value, alpha: 1 };
  return { hex: "#000000", alpha: 1 };
}

function buildColorValue(hex, alpha) {
  const full = /^#[0-9a-fA-F]{3}$/.test(hex)
    ? "#" + hex.slice(1).split("").map((c) => c + c).join("")
    : hex;
  if (alpha >= 1 || !/^#[0-9a-fA-F]{6}$/.test(full)) return full;
  const r = parseInt(full.slice(1, 3), 16);
  const g = parseInt(full.slice(3, 5), 16);
  const b = parseInt(full.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${parseFloat(alpha.toFixed(2))})`;
}

const DEMO_SHELL_CSS = `
  #demo-settings-toggle {
    position: fixed;
    top: 8px;
    right: 8px;
    z-index: 10001;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    border: none;
    background: #1e1e2e;
    color: #fff;
    font-size: 16px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 8px rgba(0,0,0,0.35);
    transition: background 0.15s;
    line-height: 1;
    padding: 0;
  }
  #demo-settings-toggle:hover { background: #374151; }

  #demo-settings-panel {
    position: fixed;
    top: 0;
    right: -340px;
    width: 320px;
    height: 100%;
    z-index: 10000;
    background: #1e1e2e;
    color: #e2e8f0;
    box-shadow: -4px 0 20px rgba(0,0,0,0.4);
    overflow-y: auto;
    transition: right 0.25s ease;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    font-size: 12px;
  }
  #demo-settings-panel.open { right: 0; }

  .dsp-header {
    padding: 14px 16px 10px;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.04em;
    border-bottom: 1px solid #2d2d44;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .dsp-header span { opacity: 0.45; font-weight: 400; font-size: 11px; }

  .dsp-section {
    padding: 10px 16px 4px;
    border-bottom: 1px solid #2d2d44;
  }
  .dsp-section-label {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    opacity: 0.45;
    margin-bottom: 8px;
  }
  .dsp-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
    gap: 8px;
  }
  .dsp-row label {
    flex: 0 0 100px;
    font-size: 11px;
    opacity: 0.75;
    white-space: normal;
    line-height: 1.3;
  }
  .dsp-row input[type="text"],
  .dsp-row input[type="number"] {
    flex: 1;
    background: #2a2a3e;
    border: 1px solid #3a3a55;
    border-radius: 4px;
    color: #e2e8f0;
    font-size: 11px;
    padding: 4px 7px;
    min-width: 0;
    outline: none;
    transition: border-color 0.12s;
  }
  .dsp-row input[type="text"]:focus,
  .dsp-row input[type="number"]:focus { border-color: #6366f1; }

  .dsp-color-wrap {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
    min-width: 0;
  }
  .dsp-color-top {
    display: flex;
    align-items: center;
    gap: 5px;
  }
  .dsp-color-wrap input[type="color"] {
    width: 26px;
    height: 26px;
    padding: 1px;
    border: 1px solid #3a3a55;
    border-radius: 4px;
    background: #2a2a3e;
    cursor: pointer;
    flex: 0 0 26px;
  }
  .dsp-color-wrap input[type="text"] {
    flex: 1;
    background: #2a2a3e;
    border: 1px solid #3a3a55;
    border-radius: 4px;
    color: #e2e8f0;
    font-size: 11px;
    padding: 4px 7px;
    min-width: 0;
    outline: none;
    transition: border-color 0.12s;
    font-family: monospace;
  }
  .dsp-color-wrap input[type="text"]:focus { border-color: #6366f1; }

  .dsp-opacity-row {
    display: flex;
    align-items: center;
    gap: 5px;
  }
  .dsp-opacity-track {
    flex: 1;
    height: 10px;
    border-radius: 5px;
    position: relative;
    border: 1px solid #3a3a55;
  }
  .dsp-opacity-range {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    margin: 0;
    padding: 0;
    -webkit-appearance: none;
    appearance: none;
    background: transparent;
    cursor: pointer;
  }
  .dsp-opacity-range::-webkit-slider-runnable-track { background: transparent; height: 10px; }
  .dsp-opacity-range::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 14px;
    height: 14px;
    margin-top: -2px;
    border-radius: 50%;
    background: #fff;
    border: 2px solid rgba(0,0,0,0.35);
    box-shadow: 0 1px 4px rgba(0,0,0,0.5);
    cursor: pointer;
  }
  .dsp-opacity-range::-moz-range-track { background: transparent; height: 10px; border-radius: 5px; }
  .dsp-opacity-range::-moz-range-thumb {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #fff;
    border: 2px solid rgba(0,0,0,0.35);
    box-shadow: 0 1px 4px rgba(0,0,0,0.5);
    cursor: pointer;
  }
  .dsp-opacity-pct {
    font-size: 10px;
    opacity: 0.6;
    min-width: 26px;
    text-align: right;
    font-family: monospace;
    flex-shrink: 0;
  }

  #demo-top-bar {
    position: fixed;
    top: 0; left: 0; right: 0;
    z-index: 1000;
    align-items: center;
    padding: 0 16px;
  }
  #demo-top-bar-label {
    font-size: 11px; font-weight: 600;
    letter-spacing: 0.04em; text-transform: uppercase; opacity: 0.55;
  }
  #demo-bottom-bar {
    position: fixed;
    bottom: 0; left: 0; right: 0;
    z-index: 1000;
    align-items: center;
    padding: 0 16px;
  }
  #demo-bottom-bar-label {
    font-size: 11px; font-weight: 600;
    letter-spacing: 0.04em; text-transform: uppercase; opacity: 0.55;
  }

  #demo-badge {
    position: fixed;
    right: 48px;
    z-index: 9999;
    background: rgba(0,0,0,0.65);
    color: #fff; font-size: 10px;
    padding: 3px 7px; border-radius: 4px;
    pointer-events: none;
    font-family: monospace; letter-spacing: 0.02em;
  }
`;

const STORAGE_KEY = "demo-shell-config";

function buildInitialConfig(params) {
  let stored = {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) stored = JSON.parse(raw);
  } catch (_) {}

  const cfg = {};
  Object.keys(DEFAULTS).forEach((k) => {
    const v = params.get(k);
    if (v !== null && v !== "") cfg[k] = v;
    else if (stored[k] !== undefined) cfg[k] = stored[k];
    else cfg[k] = DEFAULTS[k];
  });
  return cfg;
}

const ColorField = ({ fieldKey, config, onChange }) => {
  const { hex, alpha } = parseColorValue(config[fieldKey]);
  const pickerValue = /^#[0-9a-fA-F]{6}$/.test(hex) ? hex : "#000000";

  const handleColorChange = (newHex) => {
    onChange(fieldKey, buildColorValue(newHex, alpha));
  };

  const handleAlphaChange = (newAlpha) => {
    onChange(fieldKey, buildColorValue(pickerValue, parseFloat(newAlpha)));
  };

  return (
    <div className="dsp-color-wrap">
      <div className="dsp-color-top">
        <input
          type="color"
          value={pickerValue}
          onChange={(e) => handleColorChange(e.target.value)}
        />
        <input
          type="text"
          value={config[fieldKey]}
          maxLength={25}
          onChange={(e) => onChange(fieldKey, e.target.value)}
        />
      </div>
      <div className="dsp-opacity-row">
        <div
          className="dsp-opacity-track"
          style={{ background: `linear-gradient(to right, transparent, ${pickerValue})` }}
        >
          <input
            type="range"
            className="dsp-opacity-range"
            min="0"
            max="1"
            step="0.01"
            value={alpha}
            onChange={(e) => handleAlphaChange(e.target.value)}
          />
        </div>
        <span className="dsp-opacity-pct">{Math.round(alpha * 100)}%</span>
      </div>
    </div>
  );
};

const DemoShellInner = () => {
  const params = new URLSearchParams(window.location.search);

  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [config, setConfig] = useState(() => buildInitialConfig(params));

  const panelRef = useRef(null);
  const toggleRef = useRef(null);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
    } catch (_) {}
  }, [config]);

  const applyWrapperStyles = useCallback((wrapper, botH = 0) => {
    wrapper.style.maxWidth     = config.cfMaxWidth     || "";
    wrapper.style.padding      = config.cfPadding      || "";
    wrapper.style.border       = config.cfBorder       || "";
    wrapper.style.borderRadius = config.cfBorderRadius || "";
    wrapper.style.boxShadow    = config.cfBoxShadow    || "";
    // Use individual margin properties to avoid shorthand overwriting marginBottom
    wrapper.style.marginLeft   = config.cfMaxWidth ? "auto" : "";
    wrapper.style.marginRight  = config.cfMaxWidth ? "auto" : "";
    wrapper.style.marginTop    = config.cfMaxWidth ? "0" : "";
    wrapper.style.marginBottom = botH > 0 ? botH + "px" : "";
  }, [config]);

  const clearWrapperStyles = (wrapper) => {
    wrapper.style.maxWidth     = "";
    wrapper.style.padding      = "";
    wrapper.style.border       = "";
    wrapper.style.borderRadius = "";
    wrapper.style.boxShadow    = "";
    wrapper.style.marginLeft   = "";
    wrapper.style.marginRight  = "";
    wrapper.style.marginTop    = "";
    wrapper.style.marginBottom = "";
  };

  useEffect(() => {
    const topH = parseInt(config.topBarHeight || "0", 10) || 0;
    const botH = parseInt(config.bottomBarHeight || "0", 10) || 0;

    document.body.style.backgroundColor = config.bgColor || "#ffffff";
    // paddingTop offsets body content below the fixed top bar
    document.body.style.paddingTop      = topH > 0 ? topH + "px" : "0";
    // Do NOT pad body bottom – instead push only the widget wrapper up via marginBottom

    // Apply immediately if coverage-finder is already in the DOM
    const cfEl = document.querySelector("coverage-finder");
    if (cfEl && cfEl.parentElement) {
      applyWrapperStyles(cfEl.parentElement, botH);
    }

    // Also watch for coverage-finder being added later (e.g. after token loads)
    const observer = new MutationObserver(() => {
      const el = document.querySelector("coverage-finder");
      if (el && el.parentElement) {
        applyWrapperStyles(el.parentElement, botH);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      document.body.style.backgroundColor = "";
      document.body.style.paddingTop      = "";
      // Clean up wrapper styles on unmount
      const el = document.querySelector("coverage-finder");
      if (el && el.parentElement) {
        clearWrapperStyles(el.parentElement);
      }
    };
  }, [config, applyWrapperStyles]);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (
        isPanelOpen &&
        panelRef.current &&
        !panelRef.current.contains(e.target) &&
        e.target !== toggleRef.current
      ) {
        setIsPanelOpen(false);
      }
    };
    document.addEventListener("click", handleOutsideClick);
    return () => document.removeEventListener("click", handleOutsideClick);
  }, [isPanelOpen]);

  const handleFieldChange = useCallback((key, value) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
  }, []);

  const topH = parseInt(config.topBarHeight || "0", 10) || 0;
  const botH = parseInt(config.bottomBarHeight || "0", 10) || 0;
  const isActive =
    topH > 0 ||
    botH > 0 ||
    config.cfBorder ||
    config.cfBorderRadius ||
    config.cfBoxShadow ||
    config.cfMaxWidth ||
    config.cfPadding ||
    (config.bgColor && config.bgColor.toLowerCase() !== "#ffffff");

  return ReactDOM.createPortal(
    <>
      <style>{DEMO_SHELL_CSS}</style>

      <button
        ref={toggleRef}
        id="demo-settings-toggle"
        title="Demo shell settings"
        onClick={() => setIsPanelOpen((o) => !o)}
      >
        ⚙
      </button>

      <div
        ref={panelRef}
        id="demo-settings-panel"
        className={isPanelOpen ? "open" : ""}
      >
        <div className="dsp-header">
          Demo Shell <span>live client simulator</span>
        </div>

        <div className="dsp-section">
          <div className="dsp-section-label">Page</div>
          <div className="dsp-row">
            <label>Background color</label>
            <ColorField fieldKey="bgColor" config={config} onChange={handleFieldChange} />
          </div>
        </div>

        <div className="dsp-section">
          <div className="dsp-section-label">Top bar</div>
          <div className="dsp-row">
            <label>Height (px, 0 = hidden)</label>
            <input
              type="number"
              min="0"
              max="200"
              placeholder="0"
              value={config.topBarHeight}
              onChange={(e) => handleFieldChange("topBarHeight", e.target.value)}
            />
          </div>
          <div className="dsp-row">
            <label>Background color</label>
            <ColorField fieldKey="topBarColor" config={config} onChange={handleFieldChange} />
          </div>
          <div className="dsp-row">
            <label>Text color</label>
            <ColorField fieldKey="topBarTextColor" config={config} onChange={handleFieldChange} />
          </div>
          <div className="dsp-row">
            <label>Label text</label>
            <input
              type="text"
              placeholder="Client header"
              value={config.topBarLabel}
              onChange={(e) => handleFieldChange("topBarLabel", e.target.value)}
            />
          </div>
        </div>

        <div className="dsp-section">
          <div className="dsp-section-label">Bottom bar</div>
          <div className="dsp-row">
            <label>Height (px, 0 = hidden)</label>
            <input
              type="number"
              min="0"
              max="200"
              placeholder="0"
              value={config.bottomBarHeight}
              onChange={(e) => handleFieldChange("bottomBarHeight", e.target.value)}
            />
          </div>
          <div className="dsp-row">
            <label>Background color</label>
            <ColorField fieldKey="bottomBarColor" config={config} onChange={handleFieldChange} />
          </div>
          <div className="dsp-row">
            <label>Text color</label>
            <ColorField fieldKey="bottomBarTextColor" config={config} onChange={handleFieldChange} />
          </div>
          <div className="dsp-row">
            <label>Label text</label>
            <input
              type="text"
              placeholder="Client footer"
              value={config.bottomBarLabel}
              onChange={(e) => handleFieldChange("bottomBarLabel", e.target.value)}
            />
          </div>
        </div>

        <div className="dsp-section">
          <div className="dsp-section-label">CF widget wrapper</div>
          <div className="dsp-row">
            <label>Max width (vw, px, %)</label>
            <input
              type="text"
              placeholder="100%"
              value={config.cfMaxWidth}
              onChange={(e) => handleFieldChange("cfMaxWidth", e.target.value)}
            />
          </div>
          <div className="dsp-row">
            <label>Padding (vw, px, %)</label>
            <input
              type="text"
              placeholder="0"
              value={config.cfPadding}
              onChange={(e) => handleFieldChange("cfPadding", e.target.value)}
            />
          </div>
          <div className="dsp-row">
            <label>Border</label>
            <input
              type="text"
              placeholder="1px solid #ccc"
              value={config.cfBorder}
              onChange={(e) => handleFieldChange("cfBorder", e.target.value)}
            />
          </div>
          <div className="dsp-row">
            <label>Border radius (This is for rounding corners)</label>
            <input
              type="text"
              placeholder="8px"
              value={config.cfBorderRadius}
              onChange={(e) => handleFieldChange("cfBorderRadius", e.target.value)}
            />
          </div>
          <div className="dsp-row">
            <label>Box shadow (This controls the drop shadow)</label>
            <input
              type="text"
              placeholder="0 2px 10px rgba(0,0,0,.15)"
              value={config.cfBoxShadow}
              onChange={(e) => handleFieldChange("cfBoxShadow", e.target.value)}
            />
          </div>
        </div>
      </div>

      {topH > 0 && (
        <div
          id="demo-top-bar"
          style={{
            display: "flex",
            height: topH + "px",
            backgroundColor: config.topBarColor || "#333333",
          }}
        >
          <span
            id="demo-top-bar-label"
            style={{ color: config.topBarTextColor || "#ffffff" }}
          >
            {config.topBarLabel || "Client header"}
          </span>
        </div>
      )}

      {botH > 0 && (
        <div
          id="demo-bottom-bar"
          style={{
            display: "flex",
            height: botH + "px",
            backgroundColor: config.bottomBarColor || "#333333",
          }}
        >
          <span
            id="demo-bottom-bar-label"
            style={{ color: config.bottomBarTextColor || "#ffffff" }}
          >
            {config.bottomBarLabel || "Client footer"}
          </span>
        </div>
      )}

      {isActive && (
        <div
          id="demo-badge"
          style={{ bottom: botH > 0 ? botH + 6 + "px" : "6px" }}
        >
          demo-shell: custom
        </div>
      )}
    </>,
    document.body
  );
};

const DemoShell = () => {
  const params = new URLSearchParams(window.location.search);
  const editValue = [...params.entries()]
    .find(([k]) => k.toLowerCase() === "edit")?.[1];
  const isEditMode = editValue?.toLowerCase() === "true";
  if (!isEditMode) return null;
  return <DemoShellInner />;
};

export default DemoShell;
